/**
 * Lean React-inspired base component.
 *
 * Lifecycle:
 *   new C(props) → onBeforeMount() [async, fetch here] → render() → DOM inserted
 *   → onMounted() [attach listeners, mount children]
 *   setState(patch) → render() again → onMounted() re-fires
 *   unmount() → onBeforeUnmount() → children unmounted → element removed
 *
 * render() returns an HTML string built from this.props + this.state.
 */
export default class Component {
  constructor(props = {}) {
    this.props = props;
    this.state = {};
    this.el = null;
    this.children = new Set();
    this._listeners = [];
    this._mounted = false;
  }

  // --- overridable hooks ---
  async onBeforeMount() {}
  render() {
    return "";
  }
  onMounted() {}
  onBeforeUnmount() {}

  /** Mount into a container element (appends). */
  async mount(container) {
    await this.onBeforeMount();
    this.el = this._build();
    container.appendChild(this.el);
    this._mounted = true;
    this.onMounted();
    return this.el;
  }

  _build() {
    const wrapper = document.createElement("div");
    wrapper.innerHTML = this.render();
    // Unwrap single-root templates so the component owns one element.
    return wrapper.children.length === 1 ? wrapper.firstElementChild : wrapper;
  }

  /** Merge state and re-render in place. */
  setState(patch) {
    Object.assign(this.state, patch);
    if (!this._mounted || !this.el) return;
    this._teardown();
    const next = this._build();
    this.el.replaceWith(next);
    this.el = next;
    this.onMounted();
  }

  /**
   * Re-render, then put a scrollable container inside this component back where
   * it was. setState() replaces the component's element outright, so anything
   * scrolled within it restarts at the top — which, in a long modal form, throws
   * the user back to the heading every time an in-place edit re-renders.
   *
   * `selector` names the scrolling element (e.g. ".modal-card"), matched inside
   * the component both before and after the render.
   */
  setStateKeepingScroll(patch, selector) {
    const scrollTop = this.$(selector)?.scrollTop ?? 0;
    this.setState(patch);
    const restore = () => {
      const container = this.$(selector);
      if (container) container.scrollTop = scrollTop;
    };
    restore();
    // ...and again once layout has settled. onMounted() may mount children
    // asynchronously (an editor, a map), so for one frame the re-rendered
    // content is shorter than it was and the browser clamps the offset we just
    // restored to that smaller height.
    requestAnimationFrame(restore);
  }

  /**
   * Re-render while holding the *page* scroll position. Sibling to
   * setStateKeepingScroll, which restores a scroll container inside the
   * component; this one restores the window.
   *
   * setState() swaps the component's element out, so for a moment the document
   * is shorter than it was and the browser clamps window.scrollY to the new
   * height. In a filterable grid that reads as the page snapping to the top
   * every time the result set shrinks.
   *
   * `behavior: "instant"` is required, not cosmetic: the stylesheet sets
   * `html { scroll-behavior: smooth }`, so a plain scrollTo would animate the
   * restore and produce the very glide this exists to prevent. If the new
   * content is genuinely shorter than the old offset the browser still clamps —
   * that floor is unavoidable, but it lands at the new bottom instead of 0.
   */
  setStateKeepingPageScroll(patch) {
    const { scrollX, scrollY } = window;
    this.setState(patch);
    const restore = () => window.scrollTo({ left: scrollX, top: scrollY, behavior: "instant" });
    restore();
    // Again once layout settles: onMounted() may mount children asynchronously,
    // so for one frame the document is shorter than its final height.
    requestAnimationFrame(restore);
  }

  /** Mount a child component into a container; auto-cleaned on unmount. */
  async mountChild(ChildClass, props, container) {
    const child = new ChildClass(props);
    this.children.add(child);
    await child.mount(container);
    return child;
  }

  unmount() {
    this._teardown();
    for (const child of this.children) child.unmount();
    this.children.clear();
    this.onBeforeUnmount();
    this.el?.remove();
    this.el = null;
    this._mounted = false;
  }

  _teardown() {
    for (const { target, type, handler } of this._listeners) {
      target.removeEventListener(type, handler);
    }
    this._listeners = [];
    for (const child of this.children) child.unmount();
    this.children.clear();
  }

  // --- DOM helpers (scoped to this.el) ---
  $(selector) {
    return this.el?.querySelector(selector);
  }
  $$(selector) {
    return Array.from(this.el?.querySelectorAll(selector) || []);
  }
  /** Add a tracked listener (removed automatically on re-render/unmount). */
  listen(target, type, handler) {
    target.addEventListener(type, handler);
    this._listeners.push({ target, type, handler });
  }

  // --- events ---
  emit(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail }));
  }
  on(name, handler) {
    this.listen(document, name, handler);
  }

  /** Escape untrusted text for safe interpolation into render() templates. */
  static escape(value) {
    return String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }
}
